import { generateMenuTitle } from "composables/useHelpers";
import type { RouteLocationNormalizedLoaded } from "vue-router";

const GA_MEASUREMENT_ID = "G-J1WBE7PNP7";
const GTAG_SCRIPT_ID = "gtag-script";

const ROLE_PRIORITY: readonly string[] = [
  "student",
  "teacher",
  "teamleader",
  "organizer",
  "manager",
  "webmaster",
];

type StoredUser = {
  role: string[];
};

type GtagFn = (...args: unknown[]) => void;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: GtagFn;
  }
}

const getStoredUser = (): StoredUser | null => {
  const raw = localStorage.getItem("user");

  if (raw === null) {
    return null;
  }

  try {
    return JSON.parse(raw) as StoredUser;
  } catch {
    return null;
  }
};

const resolveUserRole = (user: StoredUser): string => {
  for (const role of ROLE_PRIORITY) {
    if (user.role.includes(role)) {
      return role;
    }
  }

  return "onbekende rol";
};

const ensureGtagScript = (): void => {
  if (document.getElementById(GTAG_SCRIPT_ID)) {
    return;
  }

  const script = document.createElement("script");
  script.id = GTAG_SCRIPT_ID;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);
};

/**
 * Mirrors Google's embed snippet as plain function calls instead of an inline
 * `<script>` built from route/user strings, which was a code-injection sink
 * (a `'` in the URL path escaped the string literal and executed as JS).
 *
 * gtag.js reads the `arguments` object off `dataLayer`, not a plain array,
 * hence the `function` + `arguments` below.
 */
const ensureGtag = (): GtagFn => {
  if (window.gtag !== undefined) {
    return window.gtag;
  }

  window.dataLayer = window.dataLayer ?? [];

  const gtag: GtagFn = function () {
    window.dataLayer?.push(arguments);
  };

  window.gtag = gtag;
  gtag("js", new Date());

  return gtag;
};

const pushDatalayer = (
  route: RouteLocationNormalizedLoaded,
  role: string,
  schoolName: string | null,
): void => {
  const gtag = ensureGtag();

  gtag("config", GA_MEASUREMENT_ID, {
    page_title: generateMenuTitle(route),
    page_path: route.path,
  });
  gtag("set", "user_properties", {
    "Rol vd gebruiker": role,
    "School naam": schoolName ?? "",
  });
};

export const useGoogleAnalytics = () => {
  const initialize = (route: RouteLocationNormalizedLoaded): void => {
    ensureGtagScript();

    const user = getStoredUser();

    if (user === null) {
      return;
    }

    const role = resolveUserRole(user);
    const schoolName = localStorage.getItem("school_name");

    pushDatalayer(route, role, schoolName);
  };

  return { initialize };
};
